/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export interface IAdressInput {
    id?: string;
    name?: string;
    className: string;
    type: string;
    value: string;
    maxLength?: number;
    autoFocus?: boolean;
    additionalAddributes?: object;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    defaultCountryRegionId?: string;
    usSiteFlag?: boolean;
    mexicoSiteFlag?: boolean;
}

/**
 * Address input.
 * @param props - Configuration of the functional component.
 * @returns React functional component.
 */
export const AddressInputFunctionComponent: React.FC<IAdressInput> = (props: IAdressInput) => {
    const {
        id: itemId,
        name,
        className,
        type,
        value,
        maxLength,
        additionalAddributes,
        autoFocus: shouldUseAutoFocus,
        telemetryContent,
        onChange,
        defaultCountryRegionId
    } = props;
    const usSiteFlag = props.usSiteFlag ? props.usSiteFlag : undefined;
    const mexicoSiteFlag = props.mexicoSiteFlag ? props.mexicoSiteFlag : undefined;
    const payLoad = getPayloadObject('click', telemetryContent!, name!);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    //VSI customization for business-sign-up
    let numberValue = '';
    const customMaxLength = usSiteFlag && name === 'Phone' ? 14 : maxLength;
    if (name === 'Phone' && value !== undefined) {
        if (usSiteFlag) {
            const rawPhoneNumber = value.replace(/\D/g, ''); // Remove non-numeric characters
            let formattedPhoneNumber = '';
            if (rawPhoneNumber.length >= 11) {
                formattedPhoneNumber = rawPhoneNumber.substring(1);
                formattedPhoneNumber = formattedPhoneNumber?.split(' ').join('');
                // Apply formatting
                const areaCode = formattedPhoneNumber?.slice(0, 3);
                const firstPart = formattedPhoneNumber?.slice(3, 6);
                const secondPart = formattedPhoneNumber?.slice(6);
                formattedPhoneNumber = `(${areaCode}) ${firstPart}-${secondPart}`;
            } else if (mexicoSiteFlag) {
                formattedPhoneNumber = (value || '').replace(new RegExp('[<>]', 'gi'), '');
                const tempValue = formattedPhoneNumber.split(' ').join('');
                formattedPhoneNumber = tempValue.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '$1 $2 $3 $4');
            } else {
                if (rawPhoneNumber.length >= 1) {
                    formattedPhoneNumber = `(${rawPhoneNumber.slice(0, 3)}`;
                    if (rawPhoneNumber.length > 3) {
                        formattedPhoneNumber += `) ${rawPhoneNumber.slice(3, 6)}`;
                        if (rawPhoneNumber.length > 6) {
                            formattedPhoneNumber += `-${rawPhoneNumber.slice(6, 10)}`;
                        }
                    }
                }
            }
            numberValue = formattedPhoneNumber;
        } else {
            const phoneReg = /^[0-9\s]*$/;
            if (phoneReg.test(value)) {
                numberValue = value;
            } else {
                numberValue = value.slice(0, -1);
            }
        }
    } else if (name === 'ZipCode' && value !== undefined) {
        const zipCodeRegex = /^[a-zA-Z0-9_.-]*$/;
        if (zipCodeRegex.test(value)) {
            numberValue = value;
        } else {
            numberValue = value.slice(0, -1);
        }
    }
    return (
        <input
            name={name}
            id={itemId}
            className={`${className}__input ${className}__input-${type}`}
            type={type}
            autoFocus={shouldUseAutoFocus}
            value={name === 'Phone' || name === 'ZipCode' ? numberValue : value}
            placeholder={
                defaultCountryRegionId !== 'USA'
                    ? defaultCountryRegionId === 'MEX'
                        ? name === 'ZipCode'
                            ? '00000'
                            : name === 'Phone'
                            ? '00 00 0000 0000'
                            : ''
                        : name === 'ZipCode'
                        ? '0000'
                        : name === 'Phone'
                        ? '61 3 6438 6473'
                        : ''
                    : name === 'ZipCode'
                    ? '00000'
                    : name === 'Phone'
                    ? '(123) 456-7890'
                    : ''
            }
            maxLength={name === 'ZipCode' ? 10 : customMaxLength}
            {...(additionalAddributes || {})}
            {...attributes}
            onChange={onChange}
        />
    );
};

export default AddressInputFunctionComponent;
